import type { JSX, MouseEvent } from 'react'
import Chip from '@mui/material/Chip'
import Link from '@mui/material/Link'
import Tooltip from '@mui/material/Tooltip'
import { isProfileKey, parseCrmContactKey, type ContactRow } from '@profiler/shared'
import { shortLinkedin } from '../format'
import { useMe } from '../queries'

/**
 * Where a contact's key points.
 *
 * A LinkedIn profile is a link to the profile, as it always was. A person the
 * CRM holds without one has no page to open on LinkedIn; their key names a
 * Zoho record instead, so the link goes there — and says so, because "a chip
 * that looks like a URL but is not" is exactly the ambiguity this exists to
 * remove.
 */
type KeyLinkProps = Readonly<{
  contactKey: string
  crm?: ContactRow['crm'] | null
  variant?: 'body2' | 'caption'
  fontSize?: string
  stopPropagation?: boolean
}>

/**
 * How a person is identified, wherever they came from.
 *
 * Two shapes, and they are not variants of one another: somebody with a
 * LinkedIn URL gets a link to it, and somebody the CRM holds without one gets
 * a chip that says so. The second case exists because a CRM record with no
 * LinkedIn URL is a person no provider can be asked about, and showing an
 * unclickable key would say nothing about why.
 */
export function ContactKeyLink(props: KeyLinkProps): JSX.Element {
  return isProfileKey(props.contactKey) ? <ProfileLink {...props} /> : <CrmRecordChip {...props} />
}

function ProfileLink({
  contactKey,
  variant = 'body2',
  fontSize,
  stopPropagation = false,
}: KeyLinkProps): JSX.Element {
  return (
    <Link
      href={contactKey}
      target="_blank"
      rel="noopener noreferrer"
      underline="hover"
      variant={variant}
      className="mono"
      onClick={stopPropagation ? (event: MouseEvent) => event.stopPropagation() : undefined}
      sx={fontSize ? { fontSize } : undefined}
    >
      {shortLinkedin(contactKey)}
    </Link>
  )
}

function CrmRecordChip({
  contactKey,
  crm,
  variant = 'body2',
  fontSize,
  stopPropagation = false,
}: KeyLinkProps): JSX.Element {
  const me = useMe()
  const parsed = parseCrmContactKey(contactKey)
  const base = me.data?.zoho?.crmUrlBase ?? null
  const module = crm?.zoho.module ?? (parsed?.module === 'lead' ? 'Leads' : 'Contacts')
  const id = crm?.zoho.id ?? parsed?.zohoId ?? null
  const href = base && id ? `${base}/tab/${module}/${id}` : null
  const label = module === 'Leads' ? 'Zoho CRM lead' : 'Zoho CRM contact'

  return (
    <Tooltip
      title={
        href
          ? 'Open the record in Zoho CRM. This person has no LinkedIn URL on file.'
          : 'A Zoho CRM record with no LinkedIn URL on file.'
      }
    >
      <Chip
        size="small"
        variant="outlined"
        label={label}
        onClick={stopPropagation ? (event: MouseEvent) => event.stopPropagation() : undefined}
        {...(href
          ? { component: 'a', href, target: '_blank', rel: 'noopener noreferrer', clickable: true }
          : {})}
        sx={{ fontSize: fontSize ?? (variant === 'caption' ? '0.7rem' : '0.75rem') }}
      />
    </Tooltip>
  )
}
